import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Manufacturer } from "../../../../../core/models/entities/Manufacturer";
import LoadingElementIsolated from "../../../../components/loading-element/loading-element-isolated";

interface BrandsSectionProps {
  manufacturers?: Manufacturer[];
  isLoading: boolean;
  excludedIds: number[];
  onChange: (excludedIds: number[]) => void;
}

/** Above this catalog size a small client-side name filter is shown. */
const FILTER_THRESHOLD = 40;

/** One brand in the catalog overview: warm-red when excluded, neutral
 *  otherwise. Clicking toggles the exclusion. */
function BrandChip({
  brand,
  excluded,
  onToggle,
}: {
  brand: Manufacturer;
  excluded: boolean;
  onToggle: (brand: Manufacturer) => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={excluded}
      aria-label={excluded ? `Include ${brand.name}` : `Exclude ${brand.name}`}
      title={excluded ? "Click to include" : "Click to exclude"}
      onClick={() => onToggle(brand)}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs transition-colors ${
        excluded
          ? "bg-[#FAECE7] font-medium text-[#993C1D] hover:bg-[#F4DCD2]"
          : "border border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:bg-gray-50"
      }`}
    >
      {brand.name}
      {excluded && <FontAwesomeIcon icon={faXmark} />}
    </button>
  );
}

export function BrandsSection({
  manufacturers,
  isLoading,
  excludedIds,
  onChange,
}: BrandsSectionProps) {
  const [filter, setFilter] = useState("");

  function handleToggle(manufacturer: Manufacturer) {
    if (excludedIds.includes(manufacturer.id)) {
      onChange(excludedIds.filter((id) => id !== manufacturer.id));
    } else {
      onChange([...excludedIds, manufacturer.id]);
    }
  }

  if (isLoading || !manufacturers) {
    return (
      <div className="relative h-24">
        <LoadingElementIsolated transparent />
      </div>
    );
  }

  const sorted = [...manufacturers].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", "en", { sensitivity: "base" })
  );
  const visible = filter
    ? sorted.filter((m) =>
        m.name?.toLowerCase().includes(filter.toLowerCase())
      )
    : sorted;

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-gray-700">
            Excluded brands
          </h3>
          <span className="text-xs text-gray-400">
            {excludedIds.length} of {manufacturers.length} excluded
          </span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">
          Click a brand to exclude it. The customer sees the full catalog
          except the red ones.
        </p>
      </div>
      {manufacturers.length > FILTER_THRESHOLD && (
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter brands..."
          aria-label="Filter brands"
          className="w-64 rounded border border-borderGray px-2 py-1 text-xs placeholder:italic focus:border-gray-400 focus:outline-none"
        />
      )}
      <div className="flex flex-wrap items-center gap-2">
        {visible.map((brand) => (
          <BrandChip
            key={brand.id}
            brand={brand}
            excluded={excludedIds.includes(brand.id)}
            onToggle={handleToggle}
          />
        ))}
        {visible.length === 0 && (
          <p className="text-xs italic text-gray-400">
            No brands match the filter
          </p>
        )}
      </div>
    </div>
  );
}
